import { Suspense, useEffect, useRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';

import { PixieProvider } from '@/features/pixie/PixieContext';
import { PixiePanel } from '@/features/pixie/PixiePanel';
import { ErrorBoundary } from '@/shared/ui/ErrorBoundary';
import { RouteError, RouteLoading } from '@/shared/ui/RouteStatus';

import styles from './AppLayout.module.css';
import { SideNav } from './SideNav';
import { TopBar } from './TopBar';

export function AppLayout(): ReactElement {
  const location = useLocation();
  const main = useRef<HTMLElement>(null);
  const { t } = useTranslation('app');

  /* The page pane is the scroller, not the window, so react-router's
     ScrollRestoration has nothing left to restore and a new route would open
     wherever the last one was left. */
  useEffect(() => {
    main.current?.scrollTo({ top: 0 });
  }, [location.key]);

  return (
    /* Pixie wraps the shell because its launcher lives in the top bar while the
       panel overlays the whole cockpit, and the conversation has to outlive
       both. The panel itself renders nothing until it is opened. */
    <PixieProvider>
      <div className={styles.app}>
        <a className="co-skiplink" href="#main">
          {t('skipToContent')}
        </a>

        <TopBar />

        <div className={styles.body}>
          <SideNav />
          <main className={styles.main} id="main" ref={main} tabIndex={-1}>
            {/* Keyed on the path so a failed screen clears itself on the next
                navigation, and so the entrance animation replays per route. */}
            <ErrorBoundary
              key={location.pathname}
              fallback={(error, reset) => (
                <RouteError error={error} reset={reset} overviewHref="/" />
              )}
            >
              <Suspense fallback={<RouteLoading />}>
                <div className={styles.view}>
                  <Outlet />
                </div>
              </Suspense>
            </ErrorBoundary>
          </main>
        </div>

        <PixiePanel />
      </div>
    </PixieProvider>
  );
}
